import { yTextToSlateElement } from '@slate-yjs/core'
import { BaseElement } from 'slate'
import * as Y from 'yjs'
import React from 'react'
import { PlatePluginComponent } from '@udecode/plate-common'

/**
 * Wraps yTextToSlateElement to ensure every element in the tree
 * has a `props` object. Yjs/CRDT sync can produce elements where
 * `props` is undefined even though the TypeScript type says it's required.
 */
export function safeYTextToSlateElement(yText: Y.XmlText): BaseElement {
  const element = yTextToSlateElement(yText)
  ensureProps(element)
  return element
}

function ensureProps(node: Record<string, any>): void {
  if (node.type && !node.props) {
    node.props = {}
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (typeof child === 'object' && child !== null) {
        ensureProps(child)
      }
    }
  }
}

/**
 * HOC that ensures element.props is always defined before rendering.
 * Yjs/CRDT sync can produce elements where props is undefined at runtime
 * even though the TypeScript type marks it as required.
 */
export function withSafeProps(
  Component: PlatePluginComponent
): PlatePluginComponent {
  const SafePropsComponent = React.forwardRef<any, any>((renderProps, ref) => {
    const el = renderProps.element
    if (el && !el.props) {
      // Element has no props — render children only so the editor tree
      // stays intact but the broken element doesn't crash.
      return renderProps.children ?? null
    }
    return React.createElement(Component, { ...renderProps, ref })
  })
  SafePropsComponent.displayName = `SafeProps(${
    (Component as any).displayName || (Component as any).name || 'Component'
  })`
  return SafePropsComponent
}

/**
 * Wraps all components in a component map with withSafeProps.
 */
export function safePlateComponents(
  components: Record<string, PlatePluginComponent>
): Record<string, PlatePluginComponent> {
  const safe: Record<string, PlatePluginComponent> = {}
  for (const [key, component] of Object.entries(components)) {
    safe[key] = withSafeProps(component)
  }
  return safe
}
